import { useQuery, UseQueryResult } from 'react-query';

import { RewardTransaction } from '@jebel/constants';

import { UserKeyFilter } from 'shared/graphql';

import { useUserRewardAccount } from './useUserRewardAccount';

export const USER_REWARD_HISTORY_QUERY_KEY = 'USER_REWARD_HISTORY';

/**
 * React hook to fetch the reward account history (transactions) for a specific user in the selected community.
 *
 * Uses React Query to asynchronously retrieve reward transaction data from the tracking API.
 * The query is enabled only if the user's reward account is available.
 *
 * @param {UserKeyFilter | null | undefined} user - The user whose reward account history should be fetched.
 * @returns {UseQueryResult<RewardTransaction[]>} The query result containing the reward transaction history.
 * @throws {Error} If the user's reward account is not available, or if the fetch fails.
 */
export function useUserRewardAccountHistory(
  user: UserKeyFilter | null | undefined,
): UseQueryResult<RewardTransaction[]> {
  const { data: account } = useUserRewardAccount(user);

  return useQuery({
    enabled: Boolean(account),
    queryKey: [USER_REWARD_HISTORY_QUERY_KEY, account?.id],

    async queryFn() {
      if (!account?.id) {
        throw new Error('No current user reward account');
      }

      const url = new URL(
        `/reward/account/${account.id}/history`,
        process.env.REACT_APP_TRACKING_API_URL,
      );

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch current user reward account history (HTTP ${response.status})`,
        );
      }

      const data = await response.json();
      return data as RewardTransaction[];
    },
  });
}
